import { CORE_RADIUS, PLOT_RADIUS, PLOT_RADIUS_MAX, type HomeType, type Plot, type VillagePath } from "@tribe/domain";
import { createNoiseGenerator } from "./noise";
import { PALETTE, getRandomizedColor } from "./voxelColor";
import type { VoxelAsset, VoxelData } from "./voxelTypes";
import { generateVoxelCottage } from "./assets/VoxelCottage";
import { generateVoxelTree } from "./assets/VoxelTree";

/**
 * The ground the members' homes stand on, outside the authored village.
 *
 * The authored slice at the centre is generated by `generateFullVoxelWorld` and never changes.
 * This builds everything beyond it: rolling ground, a flat pad under each claimed plot, and a
 * dirt path from every home back to the village. It only covers ground that is actually lived
 * on, so the world is exactly as large as the guild has made it.
 *
 * Two differences from the authored core matter for cost. That slice fills every column from
 * bedrock to the surface, which is about seventy thousand voxels for a hundred-metre square —
 * affordable once, ruinous over a world that keeps growing. This emits a **surface shell**: the
 * top voxel of each column plus enough fill to hide the drop to its lowest neighbour, which is
 * everything you can actually see and typically a fifth of the volume.
 */

export type RingTerrain = {
  voxels: VoxelData[];
  cottages: { asset: VoxelAsset; x: number; y: number; z: number; rotation: number }[];
  trees: { asset: VoxelAsset; x: number; y: number; z: number }[];
  chimneys: { x: number; y: number; z: number }[];
  homeLights: { x: number; y: number; z: number }[];
  /** Ground height lookup, for anything that needs to sit on the surface. */
  groundAt: (x: number, z: number) => number;
};

/** How far past the outermost home the ground keeps going before the world ends. */
const MARGIN = 46;
/** Half-width of the levelled pad under a home when the parcel has not been expanded. */
const PAD_RADIUS = PLOT_RADIUS;
/** How far past the pad the ground eases back to its natural height. */
const PAD_FALLOFF = 10;
/** How far in from the rim the ground starts easing down to the shore. */
const RIM_TAPER = 34;
/** The height the ground settles to before the world ends. */
const SHORE_HEIGHT = 1;
/** Half-width of a dirt path. */
const PATH_HALF_WIDTH = 2.5;

const HOUSE_STYLES = ["hobbit_hole", "tudor_cottage", "cozy_cabin"] as const;

/**
 * Home types the caller knows for certain, keyed by plot id.
 *
 * A plot in this map renders exactly what it says, and `null` means the member has a parcel but
 * has not chosen a home yet — that plot stays empty ground. Plots absent from the map fall back
 * to the position hash, which is all the bootstrap gives us about other members today.
 */
export type KnownHomeTypes = ReadonlyMap<string, HomeType | null>;

/**
 * Parcel radii the caller knows, keyed by plot id.
 *
 * The levelled pad has to match the boundary items are held inside, or expanding a parcel would
 * let things stand on ground that was never flattened. Plots absent from the map keep the
 * starting radius, which is all we know about neighbours today.
 */
export type KnownPlotRadii = ReadonlyMap<string, number>;

function key(x: number, z: number): number {
  return (x + 4096) * 8192 + (z + 4096);
}

/** Deterministic per-coordinate value, so scatter and colour never change between visits. */
function hashed(x: number, z: number, salt: number): number {
  let h = Math.imul(x + 8192, 0x27d4eb2d) ^ Math.imul(z + 8192, 0x165667b1) ^ Math.imul(salt, 0x9e3779b9);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

export function buildRingTerrain(
  plots: Plot[],
  paths: VillagePath[],
  seed: number,
  ownedPlotIds: Set<string>,
  knownHomeTypes: KnownHomeTypes = new Map(),
  knownPlotRadii: KnownPlotRadii = new Map()
): RingTerrain {
  const noise = createNoiseGenerator(seed);
  const voxels: VoxelData[] = [];
  const cottages: RingTerrain["cottages"] = [];
  const trees: RingTerrain["trees"] = [];
  const chimneys: RingTerrain["chimneys"] = [];
  const homeLights: RingTerrain["homeLights"] = [];

  // Only claimed homes exist in the world. An unclaimed plot is empty ground.
  const live = plots.filter((plot) => ownedPlotIds.has(plot.id));
  if (!live.length) {
    return { voxels, cottages, trees, chimneys, homeLights, groundAt: () => 2 };
  }

  const reach = live.reduce(
    (far, plot) => Math.max(far, Math.hypot(plot.position.x, plot.position.z)),
    CORE_RADIUS
  ) + MARGIN;
  const extent = Math.ceil(reach);

  // Pads, one per claimed home, levelled so a cottage never straddles a slope.
  const pads = live.map((plot) => ({
    x: Math.round(plot.position.x),
    z: Math.round(plot.position.z),
    y: Math.max(2, Math.round(noise.getOctave(plot.position.x + 500, plot.position.z + 500, 3, 0.45) * 12)),
    radius: knownPlotRadii.get(plot.id) ?? PAD_RADIUS
  }));

  // Path centrelines, flattened into a lookup of the cells they cover.
  const pathCells = new Set<number>();
  for (const path of paths) {
    for (let leg = 0; leg < path.points.length - 1; leg += 1) {
      const a = path.points[leg]!;
      const b = path.points[leg + 1]!;
      const span = Math.hypot(b.x - a.x, b.z - a.z);
      const steps = Math.max(1, Math.ceil(span));
      for (let step = 0; step <= steps; step += 1) {
        const t = step / steps;
        const cx = a.x + (b.x - a.x) * t;
        const cz = a.z + (b.z - a.z) * t;
        const half = Math.ceil(PATH_HALF_WIDTH);
        for (let dx = -half; dx <= half; dx += 1) {
          for (let dz = -half; dz <= half; dz += 1) {
            if (Math.hypot(dx, dz) > PATH_HALF_WIDTH) continue;
            pathCells.add(key(Math.round(cx) + dx, Math.round(cz) + dz));
          }
        }
      }
    }
  }

  // Where the ground actually stops. The outline wanders with low-frequency noise so the world
  // never reads as a drawn circle, and every consumer asks this rather than comparing to `reach`.
  const rimEdge = (x: number, z: number): number =>
    reach - 6 + (noise.get2D(x + 2000, z + 2000, 0.02) - 0.5) * 16;

  const heights = new Map<number, number>();
  const naturalHeight = (x: number, z: number): number =>
    Math.max(1, Math.round(noise.getOctave(x + 500, z + 500, 3, 0.45) * 12));

  const heightAt = (x: number, z: number): number => {
    const cached = heights.get(key(x, z));
    if (cached !== undefined) return cached;
    let height = naturalHeight(x, z);
    for (const pad of pads) {
      const distance = Math.hypot(x - pad.x, z - pad.z);
      if (distance <= pad.radius) height = pad.y;
      else if (distance <= pad.radius + PAD_FALLOFF) {
        const t = (distance - pad.radius) / PAD_FALLOFF;
        height = Math.round(pad.y * (1 - t) + height * t);
      }
    }

    // The world has to end somewhere, and it ends by lying down: over the last stretch the ground
    // eases from whatever hill it was on to a low shore, so the rim is a beach running out into
    // the fog instead of a cliff. Doing it here rather than at emit time matters — the shell fill
    // below reads its neighbours through this same function, so the slope stays solid underneath.
    const fromCentre = Math.hypot(x, z);
    const edge = rimEdge(x, z);
    if (fromCentre >= edge) {
      height = SHORE_HEIGHT;
    } else {
      const t = Math.min(1, (edge - fromCentre) / RIM_TAPER);
      const eased = t * t * (3 - 2 * t);
      height = Math.max(SHORE_HEIGHT, Math.round(SHORE_HEIGHT + (height - SHORE_HEIGHT) * eased));
    }

    heights.set(key(x, z), height);
    return height;
  };

  // Ground, as a surface shell. Every column emits its top voxel, then fills down only as far
  // as the lowest of its four neighbours — which is exactly the face a terrace shows and
  // nothing that is buried behind one.
  for (let x = -extent; x <= extent; x += 1) {
    for (let z = -extent; z <= extent; z += 1) {
      const fromCentre = Math.hypot(x, z);
      if (fromCentre > rimEdge(x, z)) continue;
      // The authored village owns the middle; leave its ground alone.
      if (fromCentre < CORE_RADIUS - 2) continue;

      const height = heightAt(x, z);
      const lowest = Math.min(
        heightAt(x + 1, z),
        heightAt(x - 1, z),
        heightAt(x, z + 1),
        heightAt(x, z - 1)
      );
      const isPath = pathCells.has(key(x, z));

      if (isPath) {
        voxels.push({
          x, y: height, z,
          color: (x + z) % 4 === 0 ? PALETTE.DIRT_MEDIUM : PALETTE.DIRT_LIGHT,
          materialType: "opaque"
        });
      } else {
        const dry = height > 8;
        voxels.push({
          x, y: height, z,
          color: dry
            ? PALETTE.GRASS_DRY
            : hashed(x, z, 3) > 0.4
              ? getRandomizedColor(PALETTE.GRASS_LUSH, 0.02)
              : PALETTE.GRASS_BRIGHT,
          materialType: "foliage"
        });
        if (hashed(x, z, 11) < 0.004 && height > 2) {
          const blooms = [PALETTE.FLOWER_RED, PALETTE.FLOWER_YELLOW, PALETTE.FLOWER_PURPLE, PALETTE.FLOWER_WHITE];
          voxels.push({
            x, y: height + 1, z,
            color: blooms[Math.floor(hashed(x, z, 13) * blooms.length)]!,
            materialType: "foliage"
          });
        }
      }

      for (let y = height - 1; y >= lowest - 1 && y >= 0; y -= 1) {
        voxels.push({
          x, y, z,
          color: y >= height - 2
            ? getRandomizedColor(PALETTE.DIRT_MEDIUM, 0.03)
            : getRandomizedColor(PALETTE.STONE_MEDIUM, 0.03),
          materialType: "opaque"
        });
      }
    }
  }

  // Homes.
  live.forEach((plot, index) => {
    const x = Math.round(plot.position.x);
    const z = Math.round(plot.position.z);
    const ground = heightAt(x, z);

    // A parcel whose owner has not chosen a home yet is levelled and empty, waiting for them.
    if (knownHomeTypes.has(plot.id) && knownHomeTypes.get(plot.id) === null) return;

    const style = knownHomeTypes.get(plot.id) ?? HOUSE_STYLES[Math.floor(hashed(x, z, 23) * HOUSE_STYLES.length)]!;
    cottages.push({
      asset: generateVoxelCottage(`plot_${plot.id}`, plot.id, { type: style }),
      x,
      y: ground + 1,
      z,
      rotation: plot.rotationY
    });
    chimneys.push({ x: x + 2, y: ground + (style === "hobbit_hole" ? 7 : 9), z: z - 1 });
    homeLights.push({ x, y: ground + 3, z: z + 2 });
    void index;
  });

  // Woodland between the homes, thinned around pads and paths so nothing grows through a roof.
  //
  // Density is the whole difference between a village with trees around it and a forest with a
  // village lost inside it. The spacing below leaves open ground between clumps on purpose, and
  // thins further toward the middle so the homes and their gardens stay the subject.
  const treeSpecies = ["oak", "oak", "cherry_blossom", "apple_tree", "autumn_birch"] as const;
  const step = 34;
  for (let x = -extent; x <= extent; x += step) {
    for (let z = -extent; z <= extent; z += step) {
      const tx = Math.round(x + (hashed(x, z, 31) * 20 - 10));
      const tz = Math.round(z + (hashed(x, z, 37) * 20 - 10));
      const fromCentre = Math.hypot(tx, tz);
      if (fromCentre < CORE_RADIUS + 10 || fromCentre > rimEdge(tx, tz) - 8) continue;
      // Sparse among the homes, thickening into woodland toward the edge of the world.
      const density = 0.25 + 0.6 * Math.min(1, (fromCentre - CORE_RADIUS) / Math.max(1, reach - CORE_RADIUS));
      if (hashed(tx, tz, 43) > density) continue;
      if (pathCells.has(key(tx, tz))) continue;
      // Clear of the parcel as it stands today and of the room it could still grow into, so an
      // expansion never leaves a tree standing in the middle of somebody's new lawn.
      if (pads.some((pad) => Math.hypot(tx - pad.x, tz - pad.z) < PLOT_RADIUS_MAX + 10)) continue;
      const species = treeSpecies[Math.floor(hashed(tx, tz, 41) * treeSpecies.length)]!;
      trees.push({
        // The species belongs in the id: the petal system finds its cherry blossoms by it.
        asset: generateVoxelTree(`tree_${species}_${tx}_${tz}`, "Tree", species),
        x: tx,
        y: heightAt(tx, tz) + 1,
        z: tz
      });
    }
  }

  return { voxels, cottages, trees, chimneys, homeLights, groundAt: heightAt };
}
